<template lang="html">
  <section
    class="m-table_content"
    :id="id"
    :draggable="draggable"
    @dragstart.stop="onComponentStartDrag($event)"
    @dragend.stop="onComponentEndDrag($event)"
    @dragover.prevent.stop
    @dragenter.stop="onComponentDragEnter($event)"
  >
    <div>
      <div>
        <div class="display-element-header">
          <div class="title table-title">
            {{ getMessageText(messages.DISP_EL_TABLE) }}
          </div>
          <settings-element
            class="m-settings_section"
            @on-settings-edit-click="onSettingsClick"
            @on-settings-delete-click="onDeleteClick"
          />
        </div>

        <div class="m-header display-element-body">
          <div class="m-header-data-source-display">
            <div class="m-header-data-source-text">
              {{ getMessageText(messages.DATA_SOURCE) }}:
            </div>
            <div class="m-header-data-source-value" :title="dataSource.name">
              {{ dataSource.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="card-description normal-wrap">
        {{ description }}
      </div>

      <table class="m-table table_bordered">
        <thead>
          <tr>
            <th
              class="m-table_header"
              v-for="(col, index) in columns"
              :key="index"
              :title="getColumnHeaderValue(col.headerExpression)"
            >
              {{ getColumnHeaderValue(col.headerExpression) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td v-for="(col, index) in columns" :key="index">
              {{ col.field.name }}
              <span v-if="col.field.format"> , "{{ col.field.format }}" </span>
              <span v-if="col.field.action">
                , {{ getUserAction(col.field.action) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <simple-modal
      :show="displaySimpleModal"
      :message="getMessageText(messages.ARE_YOU_SURE)"
      :cancel-text="getMessageText(messages.CANCEL)"
      :ok-text="getMessageText(messages.OK)"
      @on-simple-modal-cancel="onCancelDeleteElement"
      @on-simple-modal-ok="onConfirmDeleteElement"
    />
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import utils from "@/utils";
  import { MUTATION_NAME } from "@/static/global-variables";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import SettingsElement from "@/views/drag-drop-pages/components/SettingsElement";

  export default  {
    name: "table-component",
    components: {
      SimpleModal,
      SettingsElement
    },
    props: {
      id: {
        type: String
      },
      // props specific to draggable component
      allowDropzones: {
        type: Boolean,
        default: true
      },
      dropEffect: {
        type: String,
        default: dragAndDrop.DROP_EFFECT.MOVE
      },
      draggable: {
        type: Boolean,
        default: false
      },
      // props specific to this component below
      columns: {
        type: Array,
        default: function() {
          return [];
        }
      },
      dataSource: {
        type: Object,
        default: function() {
          return {};
        }
      },
      description: {
        type: String
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      deleteModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_DELETE_DRAGGABLE, data);
        },
        get() {
          return store.getters.deleteDraggableItem;
        }
      },
      editModal: {
        set(data) {
          store.dispatch(MUTATION_NAME.SET_EDIT_DRAGGABLE, data);
        },
        get() {
          return store.getters.editDraggableItem;
        }
      }
    },
    data () {
      return {
        displaySimpleModal: false
      }
    },
    methods: {
      getUserAction(action) {
        let count = action.navigation.type ? 1 : 0;
        count += action.dataActions? action.dataActions.length : 0;
        return this.replaceMessageText(this.messages.USER_ACTION_COUNT, count);
      },
      getColumnHeaderValue(exp) {
        return utils.findExpressionLabel(exp);
      },
      onSettingsClick() {
        const obj = {
          id: this.id,
          modal: "AddTableModal",
          dropEffect: dragAndDrop.DROP_EFFECT.NONE,
          props: {
            columns: JSON.parse(JSON.stringify(this.columns)),
            dataSource: JSON.parse(JSON.stringify(this.dataSource)),
            description: this.description
          }
        };
        this.editModal = {detail: obj};
      },

      onCancelDeleteElement() {
        this.displaySimpleModal = false;
      },
      onConfirmDeleteElement() {
        this.displaySimpleModal = false;
        const obj = {
          id: this.id
        };
        this.deleteModal = {detail: obj};
      },
      onDeleteClick() {
        this.displaySimpleModal = true;
      },
      /**
       * @function getMetadata
       * @returns {Object} Metadata in JSON format
      */
      getMetadata() {
        return {
          element: {
            name: this.$options.name
          },
          description: this.description,
          dataSource: this.dataSource? this.dataSource.id : "",
          type: "Table",
          columns: this.columns
        };
      },

      deserialize(serializedJson, appId) {
        const content = JSON.parse(JSON.stringify(expressionBuilder.getAppContent(appId)));
        const dataSource = content.dataElements[serializedJson.dataSource];
        this.columns = JSON.parse(JSON.stringify(serializedJson.columns));
        this.description = serializedJson.description;
        if (dataSource) {
          this.dataSource = dataSource;
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .display-element-header {
    display: flex;
    flex-direction: row;
  }
  .table-title {
    display: flex;
    justify-content: space-between;
    width: 100%;
    flex: 1;
  }
  .m-table_content {
    flex: 1;
    @include mixin-box;
    .title {
      font-weight: bold;
      font-size: 0.9rem;
    }

    box-sizing: border-box;
    padding: 0.75rem;
    position: relative;
    .m-header {
      display: flex;
    }
    .m-header-data-source-display {
      display: flex;
    }
    .m-header-data-source-text {
      white-space: nowrap;
    }
    .m-header-data-source-value {
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 1; /* number of lines to show */
      line-clamp: 1;
      -webkit-box-orient: vertical;
    }
    .m-header {
      box-sizing: border-box;
      grid-template-columns: 90px auto 3rem;
      padding: 0.2rem 0 0.5rem;

      .value {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
    }
    .m-table_header {
      max-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .m-settings_section {
      grid-column: 3;
    }
  }
  .m-shrink {
    @include mixin-shrink-layout;
  }
</style>
